import type { UploadImage } from "@shared/types/product";
import { Upload } from "lucide-react";
import * as React from "react";
import { useDropzone, type Accept, type FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { Button } from "@bcc/components/ui/button";
import { useToast } from "@bcc/hooks/use-toast";
import { cn } from "@bcc/lib/utils";
import { ACCEPTED_FILE_TYPES, MAX_UPLOAD_SIZE_MB, MIN_IMAGE_HEIGHT, MIN_IMAGE_WIDTH } from "@client-shared/lib/constants";
import { setFileUploadDropzoneTestId, setFileUploadTestId, setFileUploadThumbnailTestId } from "./file-upload.utils";
import { Thumbnail } from "./thumbnail";

export type SelectType = "multiple" | "single" | "no-select";

export interface FileUploadProps {
  /**
   * Current uploaded images (controlled)
   */
  value?: UploadImage[];
  /**
   * Callback when uploaded images change (add/delete)
   */
  onChange: (images: UploadImage[]) => void;
  /**
   * Callback when image selection changes
   */
  onSelectionChange?: (selected: UploadImage[]) => void;
  /**
   * Selection mode for thumbnails
   */
  selectType?: SelectType;
  /**
   * Enable delete button on thumbnails
   */
  deleteEnabled?: boolean;
  /**
   * Enable drag-and-drop upload area
   */
  uploadEnabled?: boolean;
  /**
   * Accepted file types (default: PNG, JPEG)
   */
  accept?: Accept;
  /**
   * Test ID for testing
   */
  testId?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Disable all upload/select interactions
   */
  disabled?: boolean;
}

export function FileUpload({
  value = [],
  onChange,
  onSelectionChange,
  selectType = "multiple",
  deleteEnabled = true,
  uploadEnabled = true,
  accept = ACCEPTED_FILE_TYPES,
  testId,
  className,
  disabled = false,
}: FileUploadProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [uploadImages, setUploadImages] = React.useState<UploadImage[]>(value);
  const [selectedImages, setSelectedImages] = React.useState<UploadImage[]>([]);
  const previewUrlsRef = React.useRef<Set<string>>(new Set());

  // Sync internal state with controlled value prop
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync internal upload list when controlled value prop changes
    setUploadImages(value);
  }, [value]);

  // Cleanup all tracked preview URLs on unmount
  React.useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const validateImageDimensions = React.useCallback(
    async (file: File): Promise<UploadImage | null> => {
      return new Promise((resolve) => {
        const image = new Image();
        const validationUrl = URL.createObjectURL(file);

        image.addEventListener("load", () => {
          const isValid = image.width >= MIN_IMAGE_WIDTH && image.height >= MIN_IMAGE_HEIGHT;

          if (isValid) {
            // Create a separate preview URL (don't reuse validation URL)
            const previewUrl = URL.createObjectURL(file);
            previewUrlsRef.current.add(previewUrl);
            // Use Object.assign to preserve the File/Blob prototype
            // (spreading loses the prototype, breaking blobToBase64)
            Object.assign(file, { preview: previewUrl });
            resolve({
              type: "file",
              file: file as File & { preview: string },
              width: image.width,
              height: image.height,
            });
          } else {
            console.log("errrrr");
            toast({
              variant: "destructive",
              title: t("components.fileUpload.toast.invalidDimensionsTitle"),
              description: t("components.fileUpload.toast.invalidDimensionsDescription", {
                fileName: file.name,
                width: image.width,
                height: image.height,
                minWidth: MIN_IMAGE_WIDTH,
                minHeight: MIN_IMAGE_HEIGHT,
              }),
              style: {
                width: file.name.length > 25 ? "600px" : "400px",
                color: "black",
                backgroundColor: "#d74854",
              },
            });
            resolve(null);
          }

          // Revoke the validation URL after checking dimensions
          URL.revokeObjectURL(validationUrl);
        });

        image.addEventListener("error", () => {
          toast({
            variant: "destructive",
            title: t("components.fileUpload.toast.failedToReadTitle"),
            description: t("components.fileUpload.toast.failedToReadDescription", {
              fileName: file.name,
            }),
          });
          URL.revokeObjectURL(validationUrl);
          resolve(null);
        });

        image.src = validationUrl;
      });
    },
    [t, toast]
  );

  const handleDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      const validatedImages = await Promise.all(acceptedFiles.map(validateImageDimensions));
      const validImages = validatedImages.filter((img): img is UploadImage => img !== null);

      if (validImages.length > 0) {
        setUploadImages((prev) => {
          const newImages = [...prev, ...validImages];
          onChange(newImages);
          return newImages;
        });
      }
    },
    [onChange, validateImageDimensions]
  );

  const handleDropRejected = React.useCallback(
    (rejections: FileRejection[]) => {
      // Consolidate multiple errors if more than 3 rejections
      if (rejections.length > 3) {
        toast({
          variant: "destructive",
          title: t("components.fileUpload.toast.multipleRejectedTitle"),
          description: t("components.fileUpload.toast.multipleRejectedDescription", {
            count: rejections.length,
          }),
        });
        return;
      }

      // Show individual errors for 3 or fewer rejections
      rejections.forEach((rejection) => {
        rejection.errors.forEach((error) => {
          let message = error.message;
          if (error.code === "file-too-large") {
            message = t("components.fileUpload.toast.fileTooLarge", {
              maxSize: MAX_UPLOAD_SIZE_MB,
            });
          } else if (error.code === "file-invalid-type") {
            message = t("components.fileUpload.toast.invalidFileType");
          }

          toast({
            variant: "destructive",
            title: t("components.fileUpload.toast.fileRejectedTitle", {
              fileName: rejection.file.name,
            }),
            description: message,
          });
        });
      });
    },
    [t, toast]
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    accept,
    maxSize: MAX_UPLOAD_SIZE_MB * 1024 * 1024,
    disabled,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    noClick: false, // Allow click to open file dialog
    noKeyboard: false, // Allow keyboard activation
  });

  const effectiveSelectType = disabled ? "no-select" : selectType;
  const effectiveDeleteEnabled = deleteEnabled && !disabled;

  const handleSelect = React.useCallback(
    (image: UploadImage, checked: boolean) => {
      if (selectType === "no-select") {
        return;
      }

      let newSelection: UploadImage[];

      if (selectType === "single") {
        newSelection = checked ? [image] : [];
      } else {
        // multiple mode
        newSelection = checked ? [...selectedImages, image] : selectedImages.filter((img) => img !== image);
      }

      setSelectedImages(newSelection);
      onSelectionChange?.(newSelection);
    },
    [selectType, selectedImages, onSelectionChange]
  );

  const handleDelete = React.useCallback(
    (image: UploadImage) => {
      // Revoke object URL if it's a file
      if (image.type === "file" && image.file?.preview) {
        URL.revokeObjectURL(image.file.preview);
        previewUrlsRef.current.delete(image.file.preview);
      }

      setUploadImages((prev) => {
        const newImages = prev.filter((img) => img !== image);
        onChange(newImages);
        return newImages;
      });

      setSelectedImages((prev) => prev.filter((img) => img !== image));
    },
    [onChange]
  );

  return (
    <div data-testid={testId ?? setFileUploadTestId("default")} className={cn("space-y-4", className)}>
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-4" role="list">
        {uploadImages.map((image, index) => (
          <li key={image.type === "url" ? image.url : `file-${image.file?.name}-${index}`}>
            <Thumbnail
              image={image}
              isSelected={selectedImages.includes(image)}
              onSelect={handleSelect}
              onDelete={handleDelete}
              selectType={effectiveSelectType}
              deleteEnabled={effectiveDeleteEnabled}
              testId={setFileUploadThumbnailTestId(String(index))}
            />
          </li>
        ))}

        {uploadEnabled && (
          <li
            {...(disabled ? {} : getRootProps())}
            data-testid={setFileUploadDropzoneTestId("default")}
            className={cn(
              "flex min-h-[13rem] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer",
              "hover:border-primary hover:bg-accent/50",
              disabled && "cursor-not-allowed opacity-50 hover:border-border hover:bg-transparent",
              isDragAccept && "border-primary bg-accent/50",
              isDragReject && "border-destructive bg-destructive/10"
            )}
          >
            <input {...getInputProps()} aria-label="upload files" disabled={disabled} />
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground text-center">{t("components.fileUpload.dropzone.dragOrBrowse")}</p>
            <Button size="sm" variant="secondary" type="button" tabIndex={-1} disabled={disabled}>
              {t("components.fileUpload.dropzone.addMedia")}
            </Button>
            <div className="text-xs text-muted-foreground text-center space-y-0.5">
              <p>{t("components.fileUpload.dropzone.acceptedFormats")}</p>
              <p>
                {t("components.fileUpload.dropzone.maxSize", {
                  maxSize: MAX_UPLOAD_SIZE_MB,
                })}
              </p>
              <p>
                {t("components.fileUpload.dropzone.minDimensions", {
                  minWidth: MIN_IMAGE_WIDTH,
                  minHeight: MIN_IMAGE_HEIGHT,
                })}
              </p>
            </div>
          </li>
        )}
      </ul>
    </div>
  );
}
